import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import LocationMapInput from "../LocationMapInput";
import LocationForm from "../LocationForm";
import ChangeActiveDriver from "../transportOrderDetailModal/ChangeActiveDriver";
import { locationIssue, validCoordinates } from "../../utils/locationValidation";
import { initialScheduledDate, parseScheduledDate } from "../../utils/orderScheduledDate";
import { orderPlateWarnings, resolveOrderVehiclePlates, orderPlateFormValue } from "../../utils/orderVehiclePlates";
import {
  Button,
  Alert,
  AutoComplete,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Steps,
  Switch,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  API_Fleet_ID_planned_route_preview,
  API_FleetTransportOrderDraft_directoryResolution,
  API_FleetTransportOrderDraft_checkpointResolution,
  API_REQUEST_HEADERS_BASEURL,
} from "../../utils/Constants";
import { AuthContext } from "../../context/AuthContext";
import CheckpointMapEditor from "../../utils/CheckpointMapEditor";
import getHumanAddress from "../../utils/getHumanAddress";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { DriverContext } from "../../context/DriverContext";
import { useGlobalToast } from "../../context/ToastProvider";
import {
  listDirectoryLocations,
  listDirectoryPartners,
  saveDirectoryLocation,
  saveDirectoryPartner,
} from "../../services/fleetDirectoryService";

const WEIGHT_UNITS = [
  { value: "KILOGRAM", label: "kg" },
  { value: "GRAM", label: "g" },
  { value: "TONNE", label: "t" },
];

const VOLUME_UNITS = [
  { value: "LITRE", label: "L" },
  { value: "CUBIC_METRE", label: "m³" },
  { value: "US_GALLON", label: "US gal" },
];

const CURRENCY_OPTIONS = ["EUR", "USD", "GBP"].map((value) => ({ value, label: value }));

function emptyInlineCustomerLocationDraft(role = "PICKUP") {
  return {
    role,
    name: "",
    address: "",
    city: "",
    countryCode: "",
    latitude: null,
    longitude: null,
  };
}

const EDOC_PROFILES = [
  { value: "AUTO", labelKey: "orderDetail.eDocsProfileAuto", fallback: "Auto (by route)" },
  { value: "ECMR", pdfTemplate: "ECMR", docType: "WAYBILL", fallback: "eCMR" },
  {
    value: "EUROPEENNE",
    pdfTemplate: "EUROPEENNE",
    docType: "WAYBILL_FRA",
    fallback: "Europeenne",
  },
  {
    value: "CARTA_DE_PORTE",
    pdfTemplate: "CARTA_DE_PORTE",
    docType: "WAYBILL_ESP",
    fallback: "Carta de porte",
  },
  {
    value: "DOCUMENTO_DE_CONTROL",
    pdfTemplate: "DOCUMENTO_DE_CONTROL",
    docType: "WAYBILL_ESP",
    fallback: "Documento de control",
  },
  {
    value: "NAKLADNI_LIST_CZ",
    pdfTemplate: "NAKLADNI_LIST_CZ",
    docType: "WAYBILL_CZE",
    fallback: "Nakladni list CZ",
  },
  {
    value: "LIEFERSCHEIN",
    pdfTemplate: "LIEFERSCHEIN",
    docType: "WAYBILL",
    fallback: "Lieferschein",
  },
];

const EDOC_PROFILE_BY_PDF = EDOC_PROFILES.reduce((acc, profile) => {
  if (profile.pdfTemplate) {
    acc[profile.pdfTemplate] = profile.value;
  }
  return acc;
}, {});

const parseStructuredGoods = (values = {}) => {
  const raw = Array.isArray(values.transfollow?.structuredGoods)
    ? values.transfollow.structuredGoods
    : [];
  return raw.map((item) => ({ ...item }));
};

const inferGenerateEDocs = ({ values = {}, mode = "create", fallback = false }) => {
  if (mode === "edit") {
    if (typeof values.generateEDocs === "boolean") return values.generateEDocs;
    if (typeof values.generateECMR === "boolean") return values.generateECMR;
    return Boolean(values.freightDocumentId);
  }
  return Boolean(fallback);
};

const inferEDocsProfile = (values = {}) => {
  const explicit = values.transfollow?.edocProfile;
  if (explicit) return explicit;
  const fromPdf = values.transfollow?.pdfTemplate;
  if (fromPdf && EDOC_PROFILE_BY_PDF[fromPdf]) {
    return EDOC_PROFILE_BY_PDF[fromPdf];
  }
  return "AUTO";
};

const inferFreightDocumentStatus = (values = {}) => {
  const explicitStatus = String(values?.edocStatus || "").toUpperCase();
  if (explicitStatus) return explicitStatus;
  if (!values?.freightDocumentId) return "";
  return "UNAVAILABLE";
};

const normalizeStructuredGood = (item = {}) =>
  Object.fromEntries(
    Object.entries(item).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

const OrderForm = ({
  initialValues = {},
  mode = "create",
  onSubmit,
  onCancel,
  drivers = [],
  defaultGenerateEDocs = false,
}) => {
  const { getFleetID, fleetData } = useContext(AuthContext);
  const driverContext = useContext(DriverContext);
  const { t } = useTranslation();
  const toast = useGlobalToast();
  const fleetId = getFleetID();
  const { hereApiKey } = useGlobalSettings();

  const [currentStep, setCurrentStep] = useState(0);
  const [locationSaveAttempted, setLocationSaveAttempted] = useState(false);
  const [tripValidationAttempted, setTripValidationAttempted] = useState(false);
  const [detailsValidationAttempted, setDetailsValidationAttempted] = useState(false);
  const [plannedRoute, setPlannedRoute] = useState([]);
  const [isGoodsDetailsOpen, setIsGoodsDetailsOpen] = useState(false);
  const [directoryPartners, setDirectoryPartners] = useState([]);
  const [directoryLocations, setDirectoryLocations] = useState([]);
  const [directoryModal, setDirectoryModal] = useState(null);
  const [directoryDraft, setDirectoryDraft] = useState({});
  const [directoryResolution, setDirectoryResolution] = useState(initialValues.directoryResolution || {});
  const [resolvingDirectoryField, setResolvingDirectoryField] = useState("");
  const [importDirectorySearch, setImportDirectorySearch] = useState({});
  const importDraftId = initialValues.importDraftId || "";

  const buildFormState = (values = {}, currentMode = mode) => {
    const structuredGoods = parseStructuredGoods(values);
    const firstStructuredGood = structuredGoods[0] || {};
    return {
      customerReference: values.customerReference || values.transportOrderRef || "",
      internalReference: values.internalReference || "",
      label: values.label || "",
      status: values.status || "PENDING",
      userId: values.userId || "",
      type: currentMode === "create" && Array.isArray(fleetData?.allowedOrderTypes) && !fleetData.allowedOrderTypes.includes(values.type) ? "" : values.type || "",
      consignee: values.transfollow?.consignee?.name || "",
      customerId: values.directoryRefs?.customerId || "",
      consignorId: values.directoryRefs?.consignorId || "",
      consigneeId: values.directoryRefs?.consigneeId || "",
      pickupLocationId: values.directoryRefs?.pickupLocationId || "",
      dropoffLocationId: values.directoryRefs?.dropoffLocationId || "",
      customer: values.customer || null,
      effectiveCarrier: values.transfollow?.carrier || null,
      consignor: values.transfollow?.consignor || null,
      consigneePartner: values.transfollow?.consignee || null,
      pickupLocation: values.transfollow?.placeOfTakingOver || null,
      dropoffLocation: values.transfollow?.placeOfDelivery || null,
      goods: firstStructuredGood.name || "",
      structuredGoods,
      trailerId: orderPlateFormValue(values.truck, 'trailerId'),
      headId: orderPlateFormValue(values.truck, 'headId'),
      activationAt: initialScheduledDate(values, currentMode),
      checkPoints: values.checkPoints || [],
      generateEDocs: inferGenerateEDocs({
        values,
        mode: currentMode,
        fallback: defaultGenerateEDocs,
      }),
      eDocsProfile: inferEDocsProfile(values),
      eDocsProfileManuallySet: Boolean(values.transfollow?.edocProfile || values.transfollow?.pdfTemplate),
      driverInstructions:
        (Object.prototype.hasOwnProperty.call(values.transfollow || {}, 'specialAgreements') ||
         (currentMode === 'edit' && (values.status === 'ACTIVE' || (values.freightDocumentId && values.edocStatus !== 'DRAFT'))))
          ? values.transfollow?.carrierToDriverInstructions || values.carrierToDriverInstructions || ''
          : String(values.transfollow?.carrierToDriverInstructions || values.carrierToDriverInstructions || '').split('\n').filter(line => /^(Devolución|Recogida|Entrega)\s+(vacío|lleno)\s*@/i.test(line.trim())).join('\n'),
      specialAgreements: values.transfollow?.specialAgreements ?? String(values.transfollow?.carrierToDriverInstructions || values.carrierToDriverInstructions || '').split('\n').filter(line => !/^(Devolución|Recogida|Entrega)\s+(vacío|lleno)\s*@|^Fecha y hora previstas:/i.test(line.trim())).join('\n'),
    };
  };

  const [form, setForm] = useState(() => buildFormState(initialValues, mode));
  const savedTruck=useRef({...initialValues.truck,headId:String(initialValues.truck?.headId || '').trim(),trailerId:String(initialValues.truck?.trailerId || '').trim()});
  const [savingActiveEdit,setSavingActiveEdit]=useState(false);
  const previousAutomaticContainerInstruction = useRef("");
  const automaticContainerInstruction = useMemo(() => {
    const checkpointIndex =
      form.type === "CONTAINER_INBOUND"
        ? 2
        : form.type === "CONTAINER_OUTBOUND"
          ? 0
          : -1;
    if (checkpointIndex < 0) return "";
    const checkpoint = form.checkPoints?.[checkpointIndex] || {};
    const location = [checkpoint.name, checkpoint.address]
      .map((value) => String(value || "").trim())
      .filter((value, index, values) => value && values.indexOf(value) === index)
      .join(", ");
    if (!location) return "";
    const label = form.type === "CONTAINER_INBOUND" ? "Devolución vacío" : "Recogida vacío";
    return `${label} @ ${location}`;
  }, [form.checkPoints, form.type]);

  useEffect(() => {
    if (mode === 'edit' && (initialValues.status === 'ACTIVE' || (initialValues.freightDocumentId && initialValues.edocStatus !== 'DRAFT'))) return;
    if (currentStep !== 2 || !automaticContainerInstruction) return;
    setForm((previous) => {
      const currentInstructions = String(previous.driverInstructions || "").trim();
      const previousAutomatic = previousAutomaticContainerInstruction.current;
      const nextInstructions = !currentInstructions || currentInstructions === previousAutomatic ||
        /^(Devolución|Recogida|Entrega)\s+(vacío|lleno)\s*@/i.test(currentInstructions)
          ? automaticContainerInstruction : currentInstructions;
      return {
        ...previous,
        driverInstructions: nextInstructions,
      };
    });
    previousAutomaticContainerInstruction.current = automaticContainerInstruction;
  }, [automaticContainerInstruction, currentStep, mode, initialValues.status, initialValues.freightDocumentId, initialValues.edocStatus]);

  const loadDirectory = async () => {
    if (!fleetId) return;
    try {
      const [partners, locations] = await Promise.all([
        listDirectoryPartners(fleetId),
        listDirectoryLocations(fleetId),
      ]);
      setDirectoryPartners(partners);
      setDirectoryLocations(locations);
    } catch (error) {
      console.error("Error loading Fleet directory:", error);
    }
  };

  useEffect(() => {
    loadDirectory();
  }, [fleetId]);

  useEffect(() => {
    setDirectoryResolution(initialValues.directoryResolution || {});
    if (mode === "edit") {
      setForm(buildFormState(initialValues, mode));
      savedTruck.current={...initialValues.truck,headId:String(initialValues.truck?.headId || '').trim(),trailerId:String(initialValues.truck?.trailerId || '').trim()};
      return;
    }
    setForm((prev) => ({
      ...prev,
      generateEDocs: inferGenerateEDocs({
        values: prev,
        mode,
        fallback: defaultGenerateEDocs,
      }),
    }));
  }, [initialValues, mode, defaultGenerateEDocs]);

  const resolveImportedDirectoryField = async (field, action, { recordId = "", payload = null } = {}) => {
    if (!fleetId || !importDraftId) return null;
    setResolvingDirectoryField(field);
    try {
      const endpoint = API_FleetTransportOrderDraft_directoryResolution
        .replace("{fleetId}", fleetId)
        .replace("{draftId}", importDraftId)
        .replace("{field}", field);
      const response = await API_REQUEST_HEADERS_BASEURL.patch(endpoint, { action, recordId, payload });
      const record = response?.data?.data;
      setDirectoryResolution(record?.directoryResolution || {});
      return record;
    } catch (error) {
      console.error("Error resolving imported directory value:", error);
      return null;
    } finally {
      setResolvingDirectoryField("");
    }
  };

  const resolveImportedCheckpoint = async (checkpointIndex, action, { recordId = "", payload = null } = {}) => {
    if (!fleetId || !importDraftId) return null;
    const stateKey = `checkpoint:${checkpointIndex}`;
    setResolvingDirectoryField(stateKey);
    try {
      const endpoint = API_FleetTransportOrderDraft_checkpointResolution
        .replace("{fleetId}", fleetId)
        .replace("{draftId}", importDraftId)
        .replace("{checkpointIndex}", checkpointIndex);
      const response = await API_REQUEST_HEADERS_BASEURL.patch(endpoint, { action, recordId, payload });
      const record = response?.data?.data;
      setDirectoryResolution(record?.directoryResolution || {});
      if (record?.draft?.checkPoints) {
        setForm((current) => ({
          ...current,
          checkPoints: record.draft.checkPoints,
          pickupLocationId: record.draft.directoryRefs?.pickupLocationId || "",
          dropoffLocationId: record.draft.directoryRefs?.dropoffLocationId || "",
          pickupLocation: record.draft.transfollow?.placeOfTakingOver || null,
          dropoffLocation: record.draft.transfollow?.placeOfDelivery || null,
        }));
      }
      return record;
    } catch (error) {
      console.error("Error resolving imported checkpoint:", error);
      return null;
    } finally {
      setResolvingDirectoryField("");
    }
  };

  const driversFromContext = Array.isArray(driverContext?.driversList)
    ? driverContext.driversList
    : [];
  const selectedDriversSource = drivers.length > 0 ? drivers : driversFromContext;
  const driverOptions = selectedDriversSource
    .filter((driver) => driver?.id)
    .map((driver) => ({
      value: driver.id,
      label:
        driver.disabled && driver.disabledReason
          ? `${driver.name || driver.id} - ${driver.disabledReason}`
          : driver.name || driver.id,
      disabled: !!driver.disabled,
    }));

  const ensureCheckPointsCount = (checkPoints, type) => {
    const required = type?.startsWith("CONTAINER") ? 3 : 2;
    const filled = [...checkPoints];
    while (filled.length < required) {
      filled.push({ latitude: null, longitude: null, address: "", name: "" });
    }
    return filled;
  };

  const getOperationalCheckpointIndexes = (type, checkpointCount = 0) => {
    const lastIndex = Math.max(0, checkpointCount - 1);
    if (type === "CONTAINER_INBOUND") return { pickup: 0, dropoff: Math.min(1, lastIndex) };
    if (type === "CONTAINER_OUTBOUND") return { pickup: Math.min(1, lastIndex), dropoff: lastIndex };
    return { pickup: 0, dropoff: lastIndex };
  };

  const getCheckpointDirectoryRole = (type, index, checkpointCount, kind) => {
    if (type === "CONTAINER_INBOUND") return index === 0 ? "PICKUP" : "DROPOFF";
    if (type === "CONTAINER_OUTBOUND") return index >= checkpointCount - 1 ? "DROPOFF" : "PICKUP";
    return kind === "DELIVERY" || index > 0 ? "DROPOFF" : "PICKUP";
  };

  const getCheckpointDisplayLabel = (type, index, checkpointCount) => {
    if (type === "CONTAINER_INBOUND") {
      return [
        t("mapEditor.pickupFullContainer"),
        t("mapEditor.deliveryGoodsLocation"),
        t("mapEditor.returnEmptyContainer"),
      ][index] || t("orderImport.checkpointLabel", { index: index + 1 });
    }
    if (type === "CONTAINER_OUTBOUND") {
      return [
        t("mapEditor.pickupEmptyContainer"),
        t("mapEditor.loadingGoodsLocation"),
        t("mapEditor.returnFullContainer"),
      ][index] || t("orderImport.checkpointLabel", { index: index + 1 });
    }
    if (index === 0) return t("mapEditor.pickUp");
    if (index === checkpointCount - 1) return t("mapEditor.delivery");
    return t("orderImport.checkpointLabel", { index: index + 1 });
  };

  const applyDirectoryLocation = (kind, locationId, syncImport = true) => {
    const location = directoryLocations.find((item) => item.id === locationId);
    const field = kind === "pickup" ? "pickupLocationId" : "dropoffLocationId";
    const snapshotField = kind === "pickup" ? "pickupLocation" : "dropoffLocation";
    const routeCheckPoints = ensureCheckPointsCount(form.checkPoints || [], form.type);
    const routeIndex = getOperationalCheckpointIndexes(form.type, routeCheckPoints.length)[kind];
    setForm((prev) => {
      const checkPoints = ensureCheckPointsCount(prev.checkPoints || [], prev.type);
      const index = getOperationalCheckpointIndexes(prev.type, checkPoints.length)[kind];
      if (checkPoints[index]) {
        checkPoints[index] = location ? {
          ...checkPoints[index],
          latitude: location.latitude,
          longitude: location.longitude,
          address: location.address?.streetName || location.name,
          name: location.name,
          countryCode: location.address?.countryCode || "",
          placeId: location.id,
        } : { ...checkPoints[index], placeId: "" };
      }
      return {
        ...prev,
        [field]: locationId || "",
        [snapshotField]: location || null,
        checkPoints,
      };
    });
    if (syncImport && importDraftId && locationId) {
      if ((directoryResolution.checkpoints || []).length) {
        const checkpoint = routeCheckPoints[routeIndex] || {};
        const resolution = (directoryResolution.checkpoints || []).find((item) => Number(item.index) === Number(routeIndex));
        void resolveImportedCheckpoint(routeIndex, "SELECT", {
          recordId: locationId,
          payload: {
            name: checkpoint.name || checkpoint.address || resolution?.extracted?.name || "",
            address: checkpoint.address || resolution?.extracted?.address || "",
            latitude: checkpoint.latitude,
            longitude: checkpoint.longitude,
            code: resolution?.extracted?.code || "",
            placeTypes: resolution?.extracted?.placeTypes || [],
          },
        });
      } else {
        void resolveImportedDirectoryField(kind, "SELECT", { recordId: locationId });
      }
    }
  };

  const applyCheckpointDirectoryLocation = (checkpointIndex, locationId) => {
    const location = directoryLocations.find((item) => item.id === locationId);
    const index = Number(checkpointIndex);
    if (!Number.isFinite(index)) return;

    setForm((prev) => {
      const checkPoints = ensureCheckPointsCount(prev.checkPoints || [], prev.type);
      if (location && checkPoints[index]) {
        checkPoints[index] = {
          ...checkPoints[index],
          latitude: validCoordinates(location.latitude, location.longitude) ? Number(location.latitude) : null,
          longitude: validCoordinates(location.latitude, location.longitude) ? Number(location.longitude) : null,
          address: location.address?.formatted || location.address?.streetName || location.name,
          name: location.name,
          countryCode: location.address?.countryCode || "",
          placeId: location.id,
        };
      } else if (checkPoints[index]) {
        checkPoints[index] = { ...checkPoints[index], placeId: "" };
      }
      const operational = getOperationalCheckpointIndexes(prev.type, checkPoints.length);
      const directoryPatch = {};
      if (index === operational.pickup) {
        directoryPatch.pickupLocationId = locationId || "";
        directoryPatch.pickupLocation = location || null;
      }
      if (index === operational.dropoff) {
        directoryPatch.dropoffLocationId = locationId || "";
        directoryPatch.dropoffLocation = location || null;
      }
      return { ...prev, ...directoryPatch, checkPoints };
    });

    if (importDraftId && locationId && (directoryResolution.checkpoints || []).length) {
      const resolution = (directoryResolution.checkpoints || []).find(
        (item) => Number(item.index) === index
      );
      void resolveImportedCheckpoint(index, "SELECT", {
        recordId: locationId,
        payload: {
          name: resolution?.extracted?.name || "",
          address: resolution?.extracted?.address || "",
          code: resolution?.extracted?.code || "",
          placeTypes: resolution?.extracted?.placeTypes || [],
        },
      });
    }
  };

  const applyDirectoryPartner = (role, partnerId, syncImport = true) => {
    const partner = directoryPartners.find((item) => item.id === partnerId);
    setForm((prev) => ({
      ...prev,
      [`${role.toLowerCase()}Id`]: partnerId || "",
      ...(role === "CUSTOMER" ? { customer: partner || null } : {}),
      ...(role === "CONSIGNOR" ? { consignor: partner || null } : {}),
      ...(role === "CONSIGNEE"
        ? { consigneePartner: partner || null, consignee: partner?.name || "" }
        : {}),
    }));
    const field = role === "CUSTOMER" ? "customer" : role === "CONSIGNOR" ? "consignor" : "consignee";
    if (syncImport && importDraftId && partnerId) {
      void resolveImportedDirectoryField(field, "SELECT", { recordId: partnerId });
    }
  };

  const extendImportedPartnerRole = async (role, partnerId) => {
    if (!importDraftId || !partnerId) return;
    const field = role === "CUSTOMER" ? "customer" : role === "CONSIGNOR" ? "consignor" : "consignee";
    const record = await resolveImportedDirectoryField(field, "EXTEND_ROLE", { recordId: partnerId });
    await loadDirectory();
    const selectedId = record?.directoryResolution?.[field]?.selected?.id;
    if (selectedId) applyDirectoryPartner(role, selectedId, false);
  };

  const applyOrderPartnerOverride = (role, name) => {
    const normalizedName = String(name || "");
    setForm((prev) => {
      const snapshotField = role === "CUSTOMER" ? "customer" : role === "CONSIGNOR" ? "consignor" : "consigneePartner";
      const existingSnapshot = prev[snapshotField] || {};
      return {
        ...prev,
        [`${role.toLowerCase()}Id`]: "",
        [snapshotField]: normalizedName ? { ...existingSnapshot, name: normalizedName, legalName: normalizedName } : null,
        ...(role === "CONSIGNEE" ? { consignee: normalizedName } : {}),
      };
    });
  };


  const openPartnerCreator = (role, nameOverride = "") => {
    const field = role === "CUSTOMER" ? "customer" : role === "CONSIGNOR" ? "consignor" : "consignee";
    const extracted = directoryResolution[field]?.extracted || initialValues.extractedDirectoryValues?.[field] || {};
    setDirectoryDraft({
      roles: [role],
      name: nameOverride || extracted.name || "",
      legalName: nameOverride || extracted.legalName || extracted.name || "",
      taxId: extracted.taxId || "",
      email: extracted.email || "",
      phone: extracted.phone || "",
      orderFinalizedEmailsEnabled: false,
      orderActivatedEmailsEnabled: false,
      sharePublicDetails: true,
      pickupLocationIds: [],
      dropoffLocationIds: [],
      customerLocationDrafts: [emptyInlineCustomerLocationDraft()],
    });
    setDirectoryModal({ type: "PARTNER", role });
  };

  const openLocationCreator = (role, checkpointIndex = null, nameOverride = "") => {
    setLocationSaveAttempted(false);
    const field = role === "PICKUP" ? "pickup" : "dropoff";
    const checkpointResolution = checkpointIndex == null ? null : (directoryResolution.checkpoints || []).find((item) => Number(item.index) === Number(checkpointIndex));
    const checkpoint = checkpointIndex == null ? null : form.checkPoints?.[checkpointIndex];
    const extracted = checkpoint || checkpointResolution?.extracted || directoryResolution[field]?.extracted || initialValues.extractedDirectoryValues?.[field] || {};
    setDirectoryDraft({
      roles: [role],
      placeTypes: extracted.placeTypes || checkpointResolution?.extracted?.placeTypes || ["DELIVERY_POINT"],
      name: nameOverride || extracted.name || "",
      streetName: typeof extracted.address === "string" ? extracted.address : extracted.address?.streetName || "",
      cityName: extracted.address?.cityName || "",
      countryCode: extracted.countryCode || extracted.address?.countryCode || "",
      // Extracted coordinates are not a verified directory location.
      latitude: null,
      longitude: null,
      sharePublicDetails: true,
    });
    setDirectoryModal({ type: "LOCATION", role, checkpointIndex });
  };

  const editSelectedLocation = (id, role, checkpointIndex = null) => {
    setLocationSaveAttempted(false);
    const location = directoryLocations.find(item => item.id === id);
    if (!location) return;
    setDirectoryDraft({ ...location, ...location.address, roles: Array.from(new Set([...(location.roles || []), role])) });
    setDirectoryModal({ type: "LOCATION", role, checkpointIndex, editing: true });
  };

  const locationWarning = (id, role, checkpointIndex = null) => {
    const location = directoryLocations.find(item => item.id === id);
    if (!location) return null;
    const issue = locationIssue(location);
    return <Space direction="vertical" size={2}>
      {location.correctionPending && <Tag color="gold">{t("directory.pendingReview")}</Tag>}
      {issue && <>
        <Button size="small" title={t(`placeForm.${issue}`)} onClick={() => editSelectedLocation(id, role, checkpointIndex)}>{t("placeForm.edit")}</Button>
      </>}
    </Space>;
  };

  const saveInlineDirectoryRecord = async () => {
    if (!fleetId || !directoryModal) return;
    setLocationSaveAttempted(true);
    if (directoryModal.type === "LOCATION" && locationIssue(directoryDraft)) {
      toast({ title: t("validationReviewFields"), status: "warning" });
      return;
    }
    try {
      if (directoryModal.type === "PARTNER") {
        let partnerDraft = { ...directoryDraft };
        if (
          partnerDraft.pickupLocationIds.length + partnerDraft.dropoffLocationIds.length === 0
        ) {
          const pickupLocationIds = [];
          const dropoffLocationIds = [];
          for (const draft of (partnerDraft.customerLocationDrafts || []).filter(
            (item) => String(item?.name || "").trim()
          )) {
            const savedLocation = await saveDirectoryLocation(fleetId, {
              name: draft.name,
              roles: [draft.role],
              latitude: draft.latitude,
              longitude: draft.longitude,
              address: {
                streetName: draft.address,
                cityName: draft.city,
                postalCode: draft.postalCode || "",
                countryCode: draft.countryCode,
              },
              sharePublicDetails: partnerDraft.sharePublicDetails,
            });
            if (draft.role === "DROPOFF") dropoffLocationIds.push(savedLocation.id);
            else pickupLocationIds.push(savedLocation.id);
          }
          partnerDraft = { ...partnerDraft, pickupLocationIds, dropoffLocationIds };
        }
        const saved = await saveDirectoryPartner(fleetId, {
          ...partnerDraft,
          address: {},
        });
        await loadDirectory();
        if (importDraftId) {
          const field = directoryModal.role === "CUSTOMER" ? "customer" : directoryModal.role === "CONSIGNOR" ? "consignor" : "consignee";
          await resolveImportedDirectoryField(field, "SELECT", { recordId: saved.id });
        }
        setForm((prev) => ({
          ...prev,
          customerId: directoryModal.role === "CUSTOMER" ? saved.id : prev.customerId,
          consignorId: directoryModal.role === "CONSIGNOR" ? saved.id : prev.consignorId,
          consigneeId: directoryModal.role === "CONSIGNEE" ? saved.id : prev.consigneeId,
          ...(directoryModal.role === "CUSTOMER" ? { customer: saved } : {}),
          ...(directoryModal.role === "CONSIGNOR" ? { consignor: saved } : {}),
          ...(directoryModal.role === "CONSIGNEE" ? { consigneePartner: saved, consignee: saved.name } : {}),
        }));
      } else {
        const saved = await saveDirectoryLocation(fleetId, {
          ...directoryDraft,
          address: {
            streetName: directoryDraft.streetName,
            postalCode: directoryDraft.postalCode || "",
            cityName: directoryDraft.cityName,
            countryCode: directoryDraft.countryCode,
          },
        });
        if (saved.correctionPending) toast({ title: t("directory.pendingReview"), status: "info" });
        await loadDirectory();
        const kind = directoryModal.role === "PICKUP" ? "pickup" : "dropoff";
        if (importDraftId) {
          if (directoryModal.checkpointIndex == null) await resolveImportedDirectoryField(kind, "SELECT", { recordId: saved.id });
          else await resolveImportedCheckpoint(directoryModal.checkpointIndex, "SELECT", { recordId: saved.id });
        }
        setDirectoryLocations((prev) => [...prev.filter((item) => item.id !== saved.id), saved]);
        const index = directoryModal.checkpointIndex == null
          ? getOperationalCheckpointIndexes(form.type, ensureCheckPointsCount(form.checkPoints || [], form.type).length)[kind]
          : Number(directoryModal.checkpointIndex);
        setForm((prev) => {
          const checkPoints = ensureCheckPointsCount(prev.checkPoints || [], prev.type);
          checkPoints[index] = {
            ...checkPoints[index],
            latitude: saved.latitude,
            longitude: saved.longitude,
            address: saved.address?.streetName || saved.name,
            name: saved.name,
            countryCode: saved.address?.countryCode || "",
            placeId: saved.id,
          };
          return {
            ...prev,
            ...(directoryModal.checkpointIndex == null ? {
              [kind === "pickup" ? "pickupLocationId" : "dropoffLocationId"]: saved.id,
              [kind === "pickup" ? "pickupLocation" : "dropoffLocation"]: saved,
            } : {}),
            checkPoints,
          };
        });
      }
      setDirectoryModal(null);
    } catch (error) {
      console.error("Error saving inline directory record:", error);
      toast({ error, title: t("feedback.directorySave"), status: "error" });
    }
  };

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      checkPoints: ensureCheckPointsCount(prev.checkPoints, prev.type),
    }));
  }, [form.type]);

  useEffect(() => {
    const updateAddresses = async () => {
      if (!form.checkPoints || form.checkPoints.length === 0) return;
      const updatedCheckPoints = await Promise.all(
        form.checkPoints.map(async (checkpoint) => {
          if (checkpoint.latitude && checkpoint.longitude && !checkpoint.address) {
            try {
              const address = await getHumanAddress(
                checkpoint.latitude,
                checkpoint.longitude,
                hereApiKey
              );
              return { ...checkpoint, address };
            } catch {
              return checkpoint;
            }
          }
          return checkpoint;
        })
      );
      const hasChanges = updatedCheckPoints.some(
        (newCp, i) => newCp.address !== form.checkPoints[i]?.address
      );
      if (hasChanges) setForm((prev) => ({ ...prev, checkPoints: updatedCheckPoints }));
    };
    updateAddresses();
  }, [form.checkPoints, hereApiKey]);

  const orderTypeOptions = [
    { value: "CONTAINER_OUTBOUND", label: t("orderType.containerOutbound") },
    { value: "CONTAINER_INBOUND", label: t("orderType.containerInbound") },
    { value: "CONVENTIONAL_PICKUP", label: t("orderType.conventionalPickup") },
    { value: "CONVENTIONAL_DELIVERY", label: t("orderType.conventionalDelivery") },
  ].filter(option => !Array.isArray(fleetData?.allowedOrderTypes) || fleetData.allowedOrderTypes.includes(option.value)
    || (mode === "edit" && option.value === initialValues.type));

  const liveEdocStatus = inferFreightDocumentStatus(initialValues);
  const hasExistingEDoc = Boolean(initialValues?.freightDocumentId || initialValues?.viewPdfUrl);
  const isEDocLocked = mode === "edit" && hasExistingEDoc && liveEdocStatus !== "DRAFT";
  const goodsLines = useMemo(
    () =>
      form.structuredGoods?.length
        ? form.structuredGoods
        : [{ name: form.goods || "" }],
    [form.goods, form.structuredGoods]
  );
  const hasGoodsDetails = useMemo(
    () =>
      goodsLines.length > 1 ||
      goodsLines.some((item) =>
        Object.entries(item || {}).some(
          ([field, value]) => field !== "name" && value !== undefined && value !== null && value !== ""
        )
      ),
    [goodsLines]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "goods") {
      setForm((prev) => {
        const nextStructuredGoods = Array.isArray(prev.structuredGoods)
          ? [...prev.structuredGoods]
          : [];
        const first = { ...(nextStructuredGoods[0] || {}) };
        if (value?.trim()) {
          first.name = value;
          nextStructuredGoods[0] = first;
        } else if (Object.keys(first).length <= 1) {
          nextStructuredGoods.shift();
        } else {
          delete first.name;
          nextStructuredGoods[0] = first;
        }
        return { ...prev, goods: value, structuredGoods: nextStructuredGoods };
      });
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, activationAt: date }));
  };

  const updateStructuredGoodField = (index, field, value) => {
    setForm((prev) => {
      const nextStructuredGoods = Array.isArray(prev.structuredGoods)
        ? [...prev.structuredGoods]
        : [];
      while (nextStructuredGoods.length <= index) {
        nextStructuredGoods.push({});
      }
      const nextGood = { ...(nextStructuredGoods[index] || {}) };
      if (value === undefined || value === null || value === "") {
        delete nextGood[field];
      } else {
        nextGood[field] = value;
      }
      nextStructuredGoods[index] = nextGood;
      return {
        ...prev,
        goods: index === 0 && field === "name" ? String(value || "") : prev.goods,
        structuredGoods: nextStructuredGoods,
      };
    });
  };

  const addStructuredGood = () => {
    setForm((prev) => ({
      ...prev,
      structuredGoods: [
        ...(prev.structuredGoods?.length
          ? prev.structuredGoods
          : [{ name: prev.goods || "" }]),
        { name: "" },
      ],
    }));
  };

  const removeStructuredGood = (index) => {
    setForm((prev) => {
      const nextStructuredGoods = (prev.structuredGoods || []).filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        goods: nextStructuredGoods[0]?.name || "",
        structuredGoods: nextStructuredGoods.length ? nextStructuredGoods : [{ name: "" }],
      };
    });
  };

  const now = dayjs();
  const disabledDate = (current) => current && current < now.startOf("day");
  const disabledTime = (current) => {
    if (!current || !current.isSame(now, "day")) return {};
    return {
      disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
      disabledMinutes: (selectedHour) =>
        selectedHour === now.hour()
          ? Array.from({ length: now.minute() }, (_, i) => i)
          : [],
    };
  };

  const canMoveNext = useMemo(() => {
    if (currentStep === 0) {
      const hasLegacyParties =
        mode === "edit" &&
        !initialValues?.directoryRefs &&
        Boolean(form.consignee?.trim());
      const hasOrderParties = Boolean(
        form.customer?.name?.trim()
        && form.consignor?.name?.trim()
        && (form.consigneePartner?.name?.trim() || form.consignee?.trim())
      );
      return (
        Boolean(form.customerReference?.trim()) && Boolean(parseScheduledDate(form.activationAt)) &&
        (hasLegacyParties ||
          hasOrderParties) &&
        Boolean(form.goods?.trim()) &&
        Boolean(form.type)
      );
    }
    if (currentStep === 1) {
      const required = form.type?.startsWith("CONTAINER") ? 3 : 2;
      const basePoints = (form.checkPoints || [])
        .filter((cp) => !cp.isWaypoint)
        .slice(0, required);
      return (
        basePoints.length === required &&
        basePoints.every((cp) => validCoordinates(cp.latitude, cp.longitude)) &&
        Boolean(basePoints[0]?.name?.trim() && basePoints[basePoints.length - 1]?.name?.trim())
      );
    }
    return true;
  }, [
    currentStep,
    form.customerReference,
    form.activationAt,
    form.customerId,
    form.consignorId,
    form.consigneeId,
    form.pickupLocationId,
    form.dropoffLocationId,
    form.consignee,
    form.customer,
    form.consignor,
    form.consigneePartner,
    form.goods,
    form.type,
    form.checkPoints,
    initialValues,
    mode,
  ]);

  useEffect(() => {
    if (!fleetId) return;
    if (currentStep !== 1) return;

    const validPoints = (form.checkPoints || []).filter(
      (cp) => validCoordinates(cp.latitude, cp.longitude)
    );

    if (validPoints.length < 2) {
      setPlannedRoute([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const endpoint = API_Fleet_ID_planned_route_preview.replace("{fleetId}", fleetId);
        const response = await API_REQUEST_HEADERS_BASEURL.post(endpoint, {
          checkpoints: validPoints.map((cp) => ({
            latitude: cp.latitude,
            longitude: cp.longitude,
          })),
        });
        setPlannedRoute(response.data?.plannedRoute || []);
      } catch (error) {
        console.error("Error generating planned route preview:", error);
        setPlannedRoute([]);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [fleetId, form.checkPoints, currentStep]);

  const handleSubmit = () => {
    if (form.generateEDocs && !isEDocLocked && !String(form.driverInstructions || '').trim()) {
      toast({title:t('edocFields.instructionsRequired'),status:'warning'});
      setCurrentStep(2);
      return;
    }
    for (const [index, item] of (form.structuredGoods || []).entries()) {
      for (const [field, unit] of [['weight','weightUnit'], ['netWeight','netWeightUnit'], ['tareWeight','tareWeightUnit'], ['volume','volumeUnit'], ['commercialValue','currency']]) {
        if (item[field] !== undefined && item[field] !== null && item[field] !== '' && !String(item[unit] || '').trim()) {
          toast({ title: t(`goodsValidation.${unit}`, {row:index + 1}), status:'warning' });
          setIsGoodsDetailsOpen(true);
          return;
        }
      }
    }
    const legacyParties = mode === "edit" && !initialValues?.directoryRefs && Boolean(form.consignee?.trim());
    if (!parseScheduledDate(form.activationAt) || !form.customerReference?.trim() || !form.goods?.trim() || !form.type ||
      (!legacyParties && !(form.customer?.name?.trim() && form.consignor?.name?.trim() && (form.consigneePartner?.name?.trim() || form.consignee?.trim())))) {
      setDetailsValidationAttempted(true);
      setCurrentStep(0);
      toast({ title: t("validationReviewFields"), status: "warning" });
      return;
    }
    const requiredPoints = form.type?.startsWith("CONTAINER") ? 3 : 2;
    const tripPoints = (form.checkPoints || []).filter(point => !point.isWaypoint).slice(0, requiredPoints);
    if (tripPoints.length !== requiredPoints || tripPoints.some(point => !validCoordinates(point.latitude, point.longitude)) ||
      !tripPoints[0]?.name?.trim() || !tripPoints[tripPoints.length - 1]?.name?.trim()) {
      setTripValidationAttempted(true);
      setCurrentStep(1);
      toast({ title: t("validationReviewFields"), status: "warning" });
      return;
    }

    const fixedStatus = form.userId ? form.status : "PENDING";
    const nextStructuredGoods = Array.isArray(form.structuredGoods)
      ? [...form.structuredGoods]
      : [];
    const firstGood = { ...(nextStructuredGoods[0] || {}) };
    const goodsName = String(form.goods || "").trim();

    if (goodsName) {
      firstGood.name = goodsName;
      nextStructuredGoods[0] = firstGood;
    }

    const structuredGoods = nextStructuredGoods
      .map((item) => normalizeStructuredGood(item))
      .filter((item) => Boolean(item.name));

    const payload = {
      ...form,
      transportOrderRef: form.customerReference,
      customerReference: form.customerReference,
      internalReference: form.internalReference,
      generateEDocs: Boolean(form.generateEDocs),
      generateECMR: Boolean(form.generateEDocs),
      structuredGoods,
      goods: structuredGoods[0]?.name || "",
      ...resolveOrderVehiclePlates(form, selectedDriversSource.find(driver => driver.id === form.userId)),
      userId: form.userId ? String(form.userId).trim() : "",
      status: fixedStatus,
      directoryRefs: {
        customerId: form.customerId || "",
        consignorId: form.consignorId || "",
        consigneeId: form.consigneeId || "",
        pickupLocationId: form.pickupLocationId || "",
        dropoffLocationId: form.dropoffLocationId || "",
      },
      customer: form.customer,
      effectiveCarrier: form.effectiveCarrier,
      consignor: form.consignor,
      consigneePartner: form.consigneePartner,
      pickupLocation: form.pickupLocation,
      dropoffLocation: form.dropoffLocation,
    };

    const activeEdit=mode==='edit' && initialValues.status==='ACTIVE';
    const truck={headId:payload.headId,trailerId:payload.trailerId,plateSources:payload.plateSources};
    const platesChanged=activeEdit && ['headId','trailerId'].some(k=>truck[k]!==savedTruck.current[k]
      || truck.plateSources[k] !== (savedTruck.current.plateSources?.[k] || (savedTruck.current[k] ? 'ORDER' : 'DRIVER')));
    const scheduleChanged=activeEdit && parseScheduledDate(payload.activationAt)?.getTime()!==parseScheduledDate(initialValues.activationAt)?.getTime();
    const save=async()=>{
      setSavingActiveEdit(true);
      try {
        if(platesChanged){
          const response=await API_REQUEST_HEADERS_BASEURL.post(`/fleets/${fleetId}/transportOrder/${transportOrderId}/vehicle-plates`,{
            truck,expectedTruck:savedTruck.current,confirmed:true,
          });
          savedTruck.current=response.data.truck;
        }
        await onSubmit(payload, { directoryResolution });
      }catch(error){
        toast({title:t('activeOrderEditFailed'),status:'error',duration:8000,isClosable:true});
        throw error;
      }finally{setSavingActiveEdit(false);}
    };
    if(platesChanged || scheduleChanged){
      Modal.confirm({title:t('orderDetail.editTransportOrder'),content:t('activeOrderEditConfirmation'),
        okText:t('orderDetail.reassign.confirm'),cancelText:t('orderDetail.reassign.cancel'),onOk:save});
    }else void save().catch(()=>{});
  };

  const transportOrderId = initialValues?.id || initialValues?.transportOrderId;

  useEffect(() => {
    return undefined;
  }, []);

  const inlineCustomerEmailRequired = directoryModal?.role === "CUSTOMER" &&
    (directoryDraft.orderFinalizedEmailsEnabled === true || directoryDraft.orderActivatedEmailsEnabled === true);
  const inlineDirectorySaveDisabled =
    !directoryDraft.name ||
    (inlineCustomerEmailRequired && !directoryDraft.email);
  const importFieldDefinitions = [
    { field: "customer", role: "CUSTOMER", kind: "PARTNER", formField: "customerId" },
    { field: "consignor", role: "CONSIGNOR", kind: "PARTNER", formField: "consignorId" },
    { field: "consignee", role: "CONSIGNEE", kind: "PARTNER", formField: "consigneeId" },
    { field: "pickup", role: "PICKUP", kind: "LOCATION", formField: "pickupLocationId" },
    { field: "dropoff", role: "DROPOFF", kind: "LOCATION", formField: "dropoffLocationId" },
  ];

  const renderImportDirectoryReview = () => {
    if (!importDraftId) return null;
    const statusColor = { MATCHED: "green", SELECTED: "blue", NEW: "gold", AMBIGUOUS: "orange", MISSING: "default", ROLE_REQUIRED: "purple" };
    return (
      <div style={{ marginBottom: 16, padding: 12, border: "1px solid #d9e2f2", borderRadius: 10, background: "#fff" }}>
        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
          {t("orderImport.directoryReviewTitle")}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {t("orderImport.directoryReviewDescription")}
        </Typography.Paragraph>
        <div style={{ display: "grid", gap: 10 }}>
          {importFieldDefinitions
            .filter(({ kind }) => kind !== "LOCATION" || !(directoryResolution.checkpoints || []).length)
            .map(({ field, role, kind, formField }) => {
            const originalResolution = directoryResolution[field] || { status: "MISSING" };
            const resolution = originalResolution.status === "NEW" && originalResolution.candidates?.length
              ? { ...originalResolution, status: "AMBIGUOUS" } : originalResolution;
            const extractedName = resolution.extracted?.name || resolution.extracted?.address?.streetName || "—";
            const records = kind === "PARTNER" ? directoryPartners : directoryLocations;
            const options = records
              .filter((item) => item.roles?.includes(role))
              .map((item) => ({ value: item.id, label: item.name }));
            [resolution.selected, ...(resolution.candidates || [])].filter(Boolean).forEach((candidate) => {
              if (candidate.source !== "SHARED" && candidate.id && !options.some((option) => option.value === candidate.id)) {
                options.push({ value: candidate.id, label: candidate.name });
              }
            });
            for (const candidate of resolution.candidates || []) {
              if (candidate.source === "SHARED") {
                options.push({ value: `shared:${candidate.id}`, label: `${candidate.name} · ${t("orderImport.directorySource.SHARED")}` });
              }
            }
            return (
              <div key={field} style={{ display: "grid", gridTemplateColumns: "minmax(130px, .7fr) minmax(180px, 1fr) minmax(240px, 1.4fr) auto", gap: 8, alignItems: "center" }}>
                <Typography.Text strong>{t(`directory.role.${role}`)}</Typography.Text>
                <Typography.Text ellipsis={{ tooltip: extractedName }}>{extractedName}</Typography.Text>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={form[formField] || undefined}
                  title={t("orderImport.selectDirectoryMatch")} placeholder={t("orderImport.selectDirectoryMatch")}
                  options={options}
                  loading={resolvingDirectoryField === field}
                  onSearch={(value) => setImportDirectorySearch((current) => ({ ...current, [field]: value }))}
                  onClear={async () => {
                    await resolveImportedDirectoryField(field, "CLEAR");
                    if (kind === "PARTNER") applyDirectoryPartner(role, "", false);
                    else applyDirectoryLocation(field, "", false);
                  }}
                  onChange={async (value) => {
                    if (!value) return;
                    if (String(value).startsWith("shared:")) {
                      const record = await resolveImportedDirectoryField(field, "ADOPT_SHARED", { recordId: String(value).slice(7) });
                      await loadDirectory();
                      const selectedId = record?.directoryResolution?.[field]?.selected?.id;
                      if (selectedId) {
                        if (kind === "PARTNER") applyDirectoryPartner(role, selectedId, false);
                        else applyDirectoryLocation(field, selectedId, false);
                      }
                      return;
                    }
                    if (kind === "PARTNER") applyDirectoryPartner(role, value);
                    else applyDirectoryLocation(field, value);
                  }}
                />
                <Space size={6}>
                  <Tag color={statusColor[resolution.status]}>
                    {t(`orderImport.directoryStatus.${resolution.status}`)}
                  </Tag>
                  {resolution.source && <Tag>{t(`orderImport.directorySource.${resolution.source}`)}</Tag>}
                  {kind === "LOCATION" && locationWarning(form[formField] || resolution.selected?.id, role)}
                  {resolution.status !== "ROLE_REQUIRED" && (
                    <Button size="small" onClick={() => {
                      const typedValue = String(importDirectorySearch[field] || "").trim();
                      const suggestedValue = extractedName === "—" ? "" : extractedName;
                      if (kind === "PARTNER") openPartnerCreator(role, typedValue || suggestedValue);
                      else openLocationCreator(role, null, typedValue || suggestedValue);
                    }}>
                      {t("orderImport.createDirectoryValue")}
                    </Button>
                  )}
                  {resolution.status === "ROLE_REQUIRED" && (
                    <Button
                      size="small"
                      loading={resolvingDirectoryField === field}
                      onClick={async () => {
                        const record = await resolveImportedDirectoryField(field, "EXTEND_ROLE", { recordId: resolution.selected?.id });
                        await loadDirectory();
                        const selectedId = record?.directoryResolution?.[field]?.selected?.id;
                        if (selectedId) {
                          if (kind === "PARTNER") applyDirectoryPartner(role, selectedId, false);
                          else applyDirectoryLocation(field, selectedId, false);
                        }
                      }}
                    >
                      {t("orderImport.addDirectoryRole")}
                    </Button>
                  )}
                </Space>
              </div>
            );
          })}
          {(directoryResolution.checkpoints || []).map((resolution) => {
            const role = getCheckpointDirectoryRole(
              form.type,
              Number(resolution.index),
              directoryResolution.checkpoints.length,
              resolution.kind
            );
            const extractedName = resolution.extracted?.name || resolution.extracted?.address?.streetName || `#${Number(resolution.index) + 1}`;
            const options = directoryLocations.filter((item) => item.roles?.includes(role)).map((item) => ({ value: item.id, label: item.name }));
            [resolution.selected, ...(resolution.candidates || [])].filter(Boolean).forEach((candidate) => {
              if (candidate.id && !options.some((option) => option.value === candidate.id)) options.push({ value: candidate.id, label: candidate.name });
            });
            return (
              <div key={`checkpoint-${resolution.index}`} style={{ display: "grid", gridTemplateColumns: "minmax(130px, .7fr) minmax(180px, 1fr) minmax(240px, 1.4fr) auto", gap: 8, alignItems: "center" }}>
                <Typography.Text strong>
                  {getCheckpointDisplayLabel(
                    form.type,
                    Number(resolution.index),
                    directoryResolution.checkpoints.length
                  )}
                </Typography.Text>
                <Typography.Text ellipsis={{ tooltip: extractedName }}>{extractedName}</Typography.Text>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={resolution.selected?.id || undefined}
                  title={t("orderImport.selectDirectoryMatch")} placeholder={t("orderImport.selectDirectoryMatch")}
                  options={options}
                  loading={resolvingDirectoryField === `checkpoint:${resolution.index}`}
                  onSearch={(value) => setImportDirectorySearch((current) => ({
                    ...current,
                    [`checkpoint:${resolution.index}`]: value,
                  }))}
                  onClear={() => resolveImportedCheckpoint(resolution.index, "CLEAR")}
                  onChange={(value) => {
                    if (!value) return;
                    const checkpoint = form.checkPoints?.[Number(resolution.index)] || {};
                    return resolveImportedCheckpoint(resolution.index, "SELECT", {
                      recordId: value,
                      payload: {
                        name: checkpoint.name || checkpoint.address || resolution.extracted?.name || "",
                        address: checkpoint.address || resolution.extracted?.address || "",
                        latitude: checkpoint.latitude,
                        longitude: checkpoint.longitude,
                        code: resolution.extracted?.code || "",
                        placeTypes: resolution.extracted?.placeTypes || [],
                      },
                    });
                  }}
                />
                <Space size={6}>
                  <Tag color={statusColor[resolution.status === "NEW" && resolution.candidates?.length ? "AMBIGUOUS" : resolution.status]}>{t(`orderImport.directoryStatus.${resolution.status === "NEW" && resolution.candidates?.length ? "AMBIGUOUS" : resolution.status}`)}</Tag>
                  {locationWarning(resolution.selected?.id, role, Number(resolution.index))}
                  {resolution.source && <Tag>{t(`orderImport.directorySource.${resolution.source}`)}</Tag>}
                  <Button size="small" onClick={() => openLocationCreator(
                    role,
                    resolution.index,
                    String(importDirectorySearch[`checkpoint:${resolution.index}`] || "").trim() || extractedName
                  )}>{t("orderImport.createDirectoryValue")}</Button>
                </Space>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        borderRadius: 12,
        padding: 16,
        background: "#f7f9fc",
        border: "1px solid #e6ecf5",
      }}
    >
      {renderImportDirectoryReview()}
      <Steps
        size="small"
        current={currentStep}
        items={[
          { title: t("transportCardForm.meta", { defaultValue: "Details" }) },
          { title: t("transportCardForm.trip", { defaultValue: "Trip" }) },
          { title: t("transportCardForm.driver", { defaultValue: "Driver" }) },
        ]}
      />

      {currentStep === 0 && (
        <div style={{ display: "grid", gap: 12, marginTop: 16, maxWidth: 840, marginLeft: "auto", marginRight: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <Input
              name="customerReference"
              status={detailsValidationAttempted && !form.customerReference?.trim() ? "error" : undefined}
              value={form.customerReference}
              onChange={handleChange}
              title={detailsValidationAttempted && !form.customerReference?.trim() ? t("validationRequiredField", { field: t("orderDetail.customerReference") }) : t("orderDetail.customerReference")} placeholder={t("orderDetail.customerReference")}
              disabled={isEDocLocked}
            />
            <Input
              name="internalReference"
              value={form.internalReference}
              onChange={handleChange}
              title={t("orderDetail.internalReference")} placeholder={t("orderDetail.internalReference")}
              disabled={isEDocLocked}
            />
            <Input
              name="label"
              value={form.label}
              onChange={handleChange}
              title={t("orderDetail.label")} placeholder={t("orderDetail.label")}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            {[
              { role: "CUSTOMER", field: "customerId", label: t("directory.role.CUSTOMER") },
              { role: "CONSIGNOR", field: "consignorId", label: t("directory.role.CONSIGNOR") },
              { role: "CONSIGNEE", field: "consigneeId", label: t("directory.role.CONSIGNEE") },
            ].map(({ role, field, label }) => (
              <Space.Compact key={role} style={{ width: "100%" }}>
                <AutoComplete
                  allowClear
                  style={{ width: "100%" }}
                  status={detailsValidationAttempted && !(role === "CUSTOMER" ? form.customer?.name : role === "CONSIGNOR" ? form.consignor?.name : form.consigneePartner?.name || form.consignee)?.trim() ? "error" : undefined}
                  disabled={isEDocLocked}
                  value={form[field]
                    ? directoryPartners.find((partner) => partner.id === form[field])?.name || ""
                    : role === "CUSTOMER"
                      ? form.customer?.name || ""
                      : role === "CONSIGNOR"
                        ? form.consignor?.name || ""
                        : form.consigneePartner?.name || form.consignee || ""}
                  title={detailsValidationAttempted && !(role === "CUSTOMER" ? form.customer?.name : role === "CONSIGNOR" ? form.consignor?.name : form.consigneePartner?.name || form.consignee)?.trim() ? t("validationRequiredField", { field: label }) : label} placeholder={label}
                  onChange={(value) => applyOrderPartnerOverride(role, value)}
                  onSelect={(_, option) => {
                    if (option.hasRole) applyDirectoryPartner(role, option.id);
                  }}
                  options={directoryPartners
                    .filter((partner) => importDraftId || partner.roles?.includes(role))
                    .map((partner) => ({
                      key: partner.id,
                      value: partner.name,
                      id: partner.id,
                      hasRole: partner.roles?.includes(role),
                      label: (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <span>
                            {partner.name}
                            {role === "CUSTOMER" && partner.email ? ` — ${partner.email}` : ""}
                          </span>
                          {!partner.roles?.includes(role) && importDraftId ? (
                            <Button
                              size="small"
                              loading={resolvingDirectoryField === (role === "CUSTOMER" ? "customer" : role === "CONSIGNOR" ? "consignor" : "consignee")}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                void extendImportedPartnerRole(role, partner.id);
                              }}
                            >
                              {t("orderImport.addDirectoryRole")}
                            </Button>
                          ) : null}
                        </div>
                      ),
                    }))}
                />
                {!form[field] && (role === "CUSTOMER" ? form.customer?.name : role === "CONSIGNOR" ? form.consignor?.name : form.consigneePartner?.name) ? (
                  <Tag color="gold">{t(importDraftId && directoryResolution[role === "CUSTOMER" ? "customer" : role === "CONSIGNOR" ? "consignor" : "consignee"]?.candidates?.length
                    ? "orderImport.directoryStatus.AMBIGUOUS" : "orderImport.directoryStatus.NEW")}</Tag>
                ) : null}
                <Button
                  disabled={isEDocLocked}
                  onClick={() => openPartnerCreator(
                    role,
                    role === "CUSTOMER" ? form.customer?.name : role === "CONSIGNOR" ? form.consignor?.name : form.consigneePartner?.name
                  )}
                >
                  {!form[field] && (role === "CUSTOMER" ? form.customer?.name : role === "CONSIGNOR" ? form.consignor?.name : form.consigneePartner?.name)
                    ? t("orderImport.createDirectoryValue")
                    : "+"}
                </Button>
              </Space.Compact>
            ))}
          </div>
          <Radio.Group
            value={form.type || undefined}
            disabled={isEDocLocked || (mode === 'edit' && initialValues.status === 'ACTIVE')}
            title={detailsValidationAttempted && !form.type ? t("validationReviewFields") : undefined}
            optionType="button"
            buttonStyle="solid"
            onChange={(event) => {
              if (isEDocLocked || (mode === 'edit' && initialValues.status === 'ACTIVE')) return;
              const value = event?.target?.value || "";
              setForm((prev) => {
                const checkPoints = ensureCheckPointsCount(prev.checkPoints, value);
                const operational = getOperationalCheckpointIndexes(value, checkPoints.length);
                const pickupId = checkPoints[operational.pickup]?.placeId || "";
                const dropoffId = checkPoints[operational.dropoff]?.placeId || "";
                return {
                  ...prev, type: value, checkPoints,
                  pickupLocationId: pickupId,
                  dropoffLocationId: dropoffId,
                  pickupLocation: directoryLocations.find(item => item.id === pickupId) || null,
                  dropoffLocation: directoryLocations.find(item => item.id === dropoffId) || null,
                };
              });
            }}
            style={{ width: "100%", ...(detailsValidationAttempted && !form.type ? { outline: "1px solid #ff4d4f", borderRadius: 6 } : {}) }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
              {orderTypeOptions.map((option) => (
                <Radio.Button
                  key={option.value}
                  value={option.value}
                  style={{ textAlign: "center", width: "100%" }}
                >
                  {option.label}
                </Radio.Button>
              ))}
            </div>
          </Radio.Group>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(320px, 2fr) auto minmax(220px, 1fr)",
              gap: 12,
            }}
          >
            <Input
              name="goods"
              status={detailsValidationAttempted && !form.goods?.trim() ? "error" : undefined}
              value={form.goods}
              onChange={handleChange}
              title={detailsValidationAttempted && !form.goods?.trim() ? t("validationRequiredField", { field: t("orderDetail.goods") }) : t("orderDetail.goods")} placeholder={t("orderDetail.goods")}
            />
            <Button
              onClick={() => setIsGoodsDetailsOpen(true)}
              type={hasGoodsDetails ? "primary" : "default"}
              size="middle"
              style={{
                width: "fit-content",
                minWidth: 0,
                justifySelf: "start",
                paddingInline: 12,
                height: 32,
              }}
            >
              {t("orderDetail.structuredGoodsDetails", { defaultValue: "Goods details" })} ({goodsLines.length})
            </Button>
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              value={form.activationAt ? dayjs(form.activationAt) : null}
              status={detailsValidationAttempted && !form.activationAt ? "error" : undefined}
              title={detailsValidationAttempted && !form.activationAt ? t("validationReviewFields") : undefined}
              onChange={(value) => handleDateChange(value ? value.toDate() : null)}
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              style={{ width: "100%" }}
              disabled={isEDocLocked && initialValues.status !== 'ACTIVE'}
            />
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <div style={{ marginTop: 16, width: "100%" }}>
          <CheckpointMapEditor
            showErrors={tripValidationAttempted}
            orderDetail={{ checkPoints: form.checkPoints }}
            shipmentType={form.type}
            plannedRoute={plannedRoute}
            directoryLocations={directoryLocations}
            onDirectoryLocationSelect={applyCheckpointDirectoryLocation}
            onCreateDirectoryLocation={(checkpointIndex, point) => {
              const checkPoints = ensureCheckPointsCount(form.checkPoints || [], form.type);
              const role = getCheckpointDirectoryRole(form.type, checkpointIndex, checkPoints.length);
              openLocationCreator(role, checkpointIndex, point?.name || point?.address || "");
            }}
            onChange={(updatedCheckpoints) =>
              setForm((prev) => ({
                ...prev,
                checkPoints: updatedCheckpoints.map((p) => ({
                  latitude: p.lat,
                  longitude: p.lng,
                  address: p.address,
                  name: p.name,
                  countryCode: p.countryCode || "",
                  placeId: p.placeId || "",
                })),
              }))
            }
          />
          <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
              <Typography.Text type="secondary">
                {t("orderDetail.tripNotes", { defaultValue: "Use the map or search boxes to define the trip." })}
              </Typography.Text>
              <div
                style={{
                  display: "grid",
                  gap: 8,
                  border: "1px solid #e5e9f2",
                  borderRadius: 10,
                  padding: 10,
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Switch
                    checked={Boolean(form.generateEDocs)}
                    onChange={(checked) =>
                      setForm((prev) => ({
                        ...prev,
                        generateEDocs: checked || hasExistingEDoc,
                      }))
                    }
                    disabled={hasExistingEDoc}
                  />
                  <Typography.Text>
                    {t("orderDetail.generateEDocs", { defaultValue: "Generate e-Docs" })}
                  </Typography.Text>
                </div>
                {form.generateEDocs && (
                  <Select
                    allowClear
                    value={form.eDocsProfile}
                    options={EDOC_PROFILES.map((profile) => ({
                      value: profile.value,
                      label: t(`orderDetail.eDocsProfile.${profile.value}`, {
                        defaultValue: profile.fallback || profile.value,
                      }),
                    }))}
                    onChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        eDocsProfile: value || "",
                        eDocsProfileManuallySet: Boolean(value),
                      }))
                    }
                    disabled={isEDocLocked}
                  />
                )}
                {hasExistingEDoc && (
                  <Typography.Text type="secondary">
                    {t("orderDetail.eDocsAlreadyGenerated", {
                      defaultValue: "Already generated for this order.",
                    })}
                  </Typography.Text>
                )}
                {isEDocLocked && (
                  <Typography.Text type="secondary">
                    {t("orderDetail.eDocsLockedEditNotice", {
                      defaultValue:
                        "This e-Doc is no longer draft. Only operational fields will be updated.",
                    })}
                  </Typography.Text>
                )}
              </div>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
          {orderPlateWarnings(form, selectedDriversSource.find(driver => driver.id === form.userId)).map(({field, orderPlate, driverPlate}) => (
            <Alert key={field} type="warning" showIcon
              message={t(field === 'headId' ? 'orderDetail.truckId' : 'orderDetail.trailerId')}
              description={t('orderDetail.plateMismatchWarning', {orderPlate, driverPlate})} />
          ))}
          {mode === 'edit' && initialValues.status === 'ACTIVE' && transportOrderId ? (
            <ChangeActiveDriver asSelect fleetId={fleetId} orderId={transportOrderId}
              order={{...initialValues,userId:form.userId,truck:savedTruck.current}}
              onChanged={result=>{
                if(result){
                  savedTruck.current=result.truck;
                  setForm(prev=>({...prev,userId:result.driverId,headId:orderPlateFormValue(result.truck,'headId'),trailerId:orderPlateFormValue(result.truck,'trailerId'),status:'ACTIVE'}));
                }
              }} />
          ) : <Select
            disabled={mode === "edit" && (form.status === "ACTIVE" || isEDocLocked)}
            title={t("selectDriver")} placeholder={t("selectDriver")}
            options={driverOptions}
            allowClear
            showSearch
            optionFilterProp="label"
            value={form.userId || undefined}
            onChange={(value) => {
              setForm((prev) => ({
                ...prev,
                userId: value || "",
                status: value ? (prev.status === "ACTIVE" ? "ACTIVE" : "ASSIGNED") : "PENDING",
              }));
            }}
          />}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <Input
              name="headId"
              value={form.headId}
              onChange={handleChange}
              title={t("orderDetail.truckId")} placeholder={t("orderDetail.truckId")}
              disabled={isEDocLocked && initialValues.status !== 'ACTIVE'}
            />
            <Input
              name="trailerId"
              value={form.trailerId}
              onChange={handleChange}
              title={t("orderDetail.trailerId")} placeholder={t("orderDetail.trailerId")}
              disabled={isEDocLocked && initialValues.status !== 'ACTIVE'}
            />
          </div>
          <div>
            <Typography.Text strong>
              {t("orderDetail.driverInstructions")}
              {' '}<Typography.Text type="secondary">{t('edocFields.fixedHint')}</Typography.Text>
            </Typography.Text>
            <Input.TextArea
              name="driverInstructions"
              disabled={isEDocLocked || (mode === 'edit' && initialValues.status === 'ACTIVE')}
              value={form.driverInstructions}
              onChange={handleChange}
              title={t("orderDetail.driverInstructionsPlaceholder")} placeholder={t("orderDetail.driverInstructionsPlaceholder")}
              autoSize={{ minRows: 3, maxRows: 7 }}
            />
          </div>
          <div>
            <Typography.Text strong>{t('edocFields.agreements')}</Typography.Text>
            {' '}<Typography.Text type="secondary">{t('edocFields.editableHint')}</Typography.Text>
            <Input.TextArea name="specialAgreements" value={form.specialAgreements} onChange={handleChange}
              title={t('edocFields.agreements')} placeholder={t('edocFields.agreements')}
              maxLength={9500} autoSize={{minRows:3,maxRows:7}} />
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <Space>
          <Button onClick={onCancel}>{t("transportCardForm.cancel")}</Button>
        </Space>
        <Space>
          {currentStep > 0 && (
            <Button onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}>
              {t("transportCardForm.back", { defaultValue: "Back" })}
            </Button>
          )}
          {currentStep < 2 && (
            <Button
              type="primary"
              onClick={() => {
                if (!canMoveNext) {
                  if (currentStep === 1) setTripValidationAttempted(true);
                  else setDetailsValidationAttempted(true);
                  toast({ title: t("validationReviewFields"), status: "warning" });
                  return;
                }
                setCurrentStep((prev) => Math.min(prev + 1, 2));
              }}
            >
              {t("transportCardForm.next", { defaultValue: "Next" })}
            </Button>
          )}
          {currentStep === 2 && (
            <Button type="primary" onClick={handleSubmit} loading={savingActiveEdit}>
              {mode === "edit" ? t("transportCardForm.update") : t("transportCardForm.save")}
            </Button>
          )}
        </Space>
      </div>

      <Modal
        title={
          directoryModal?.type === "PARTNER"
            ? t("directory.addPartner")
            : t(directoryModal?.editing ? "directory.editLocation" : "directory.addLocation")
        }
        open={Boolean(directoryModal)}
        destroyOnClose
        onCancel={() => setDirectoryModal(null)}
        onOk={saveInlineDirectoryRecord}
        okButtonProps={{
          disabled: directoryModal?.type === "PARTNER" && inlineDirectorySaveDisabled,
        }}
        width={680}
      >
        {directoryModal?.type === "PARTNER" ? (
          <div style={{ display: "grid", gap: 12 }}>
            <Input
              value={directoryDraft.name || ""}
              title={t("directory.name")} placeholder={t("directory.name")}
              onChange={(event) => setDirectoryDraft((prev) => ({ ...prev, name: event.target.value }))}
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Input
                value={directoryDraft.email || ""}
                title={`${t("directory.email")}${inlineCustomerEmailRequired ? " *" : ""}`} placeholder={`${t("directory.email")}${inlineCustomerEmailRequired ? " *" : ""}`}
                onChange={(event) => setDirectoryDraft((prev) => ({ ...prev, email: event.target.value }))}
              />
              <Input
                value={directoryDraft.phone || ""}
                title={t("directory.phone")} placeholder={t("directory.phone")}
                onChange={(event) => setDirectoryDraft((prev) => ({ ...prev, phone: event.target.value }))}
              />
            </div>
            {directoryModal.role === "CUSTOMER" && (
              <Checkbox
                checked={directoryDraft.orderActivatedEmailsEnabled === true}
                onChange={(event) => setDirectoryDraft((prev) => ({ ...prev, orderActivatedEmailsEnabled: event.target.checked }))}
              >
                {t("directory.orderActivatedEmailsEnabled")}
              </Checkbox>
            )}
            {directoryModal.role === "CUSTOMER" && (
              <Checkbox
                checked={directoryDraft.orderFinalizedEmailsEnabled === true}
                onChange={(event) =>
                  setDirectoryDraft((prev) => ({
                    ...prev,
                    orderFinalizedEmailsEnabled: event.target.checked,
                  }))
                }
              >
                {t("directory.orderFinalizedEmailsEnabled")}
              </Checkbox>
            )}
            <Checkbox
              checked={directoryDraft.sharePublicDetails !== false}
              onChange={(event) =>
                setDirectoryDraft((prev) => ({
                  ...prev,
                  sharePublicDetails: event.target.checked,
                }))
              }
            >
              {t("directory.sharePublicDetails")}
            </Checkbox>
            {directoryModal.type === "PARTNER" && (
              <>
                <Typography.Text strong>{t("directory.locations")}</Typography.Text>
                <Select
                  mode="multiple"
                  allowClear
                  value={directoryDraft.pickupLocationIds || []}
                  title={t("directory.pickupLocations")} placeholder={t("directory.pickupLocations")}
                  onChange={(value) => setDirectoryDraft((prev) => ({ ...prev, pickupLocationIds: value }))}
                  options={directoryLocations.filter((item) => item.roles?.includes("PICKUP")).map((item) => ({ value: item.id, label: item.name }))}
                />
                <Select
                  mode="multiple"
                  allowClear
                  value={directoryDraft.dropoffLocationIds || []}
                  title={t("directory.dropoffLocations")} placeholder={t("directory.dropoffLocations")}
                  onChange={(value) => setDirectoryDraft((prev) => ({ ...prev, dropoffLocationIds: value }))}
                  options={directoryLocations.filter((item) => item.roles?.includes("DROPOFF")).map((item) => ({ value: item.id, label: item.name }))}
                />
                {!directoryDraft.pickupLocationIds?.length && !directoryDraft.dropoffLocationIds?.length && (
                  <div style={{ display: "grid", gap: 8, border: "1px solid #e5e9f2", borderRadius: 8, padding: 10 }}>
                    {(directoryDraft.customerLocationDrafts || []).map((draft, index) => {
                      const updateDraft = (patch) => setDirectoryDraft((prev) => ({
                        ...prev,
                        customerLocationDrafts: (prev.customerLocationDrafts || []).map(
                          (item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item
                        ),
                      }));
                      return (
                        <div key={index} style={{ display: "grid", gap: 8, borderTop: index ? "1px solid #e5e9f2" : "none", paddingTop: index ? 10 : 0 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                            <Select
                              value={draft.role}
                              onChange={(value) => updateDraft({ role: value })}
                              options={[
                                { value: "PICKUP", label: t("directory.role.PICKUP") },
                                { value: "DROPOFF", label: t("directory.role.DROPOFF") },
                              ]}
                            />
                            {(directoryDraft.customerLocationDrafts || []).length > 1 && (
                              <Button
                                danger
                                onClick={() => setDirectoryDraft((prev) => ({
                                  ...prev,
                                  customerLocationDrafts: (prev.customerLocationDrafts || []).filter((_, itemIndex) => itemIndex !== index),
                                }))}
                              >
                                −
                              </Button>
                            )}
                          </div>
                          <Input value={draft.name || ""} title={`${t("directory.name")} *`} placeholder={`${t("directory.name")} *`} onChange={(event) => updateDraft({ name: event.target.value })} />
                          <LocationMapInput value={{ ...draft, streetName: draft.address, cityName: draft.city }} onChange={next => updateDraft({ ...next, address: next.streetName, city: next.cityName })} />
                        </div>
                      );
                    })}
                    <Button
                      type="dashed"
                      onClick={() => setDirectoryDraft((prev) => ({
                        ...prev,
                        customerLocationDrafts: [...(prev.customerLocationDrafts || []), emptyInlineCustomerLocationDraft("DROPOFF")],
                      }))}
                    >
                      {t("directory.addLocation")}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <LocationForm key={`${directoryModal?.role}:${directoryModal?.checkpointIndex}`} fleetId={fleetId} editing={Boolean(directoryModal?.editing)}
            showErrors={locationSaveAttempted}
            suggestInitialAddress={Boolean(importDraftId && directoryModal)} value={directoryDraft} onChange={setDirectoryDraft} />
        )}
      </Modal>

      <Modal
        title={t("orderDetail.structuredGoodsDetails", { defaultValue: "Goods details" })}
        open={isGoodsDetailsOpen}
        onCancel={() => setIsGoodsDetailsOpen(false)}
        onOk={() => setIsGoodsDetailsOpen(false)}
        width={920}
      >
        <div style={{ display: "grid", gap: 16 }}>
          {goodsLines.map((goodsLine, index) => (
            <div
              key={`goods-line-${index}`}
              style={{
                display: "grid",
                gap: 10,
                border: "1px solid #e5e9f2",
                borderRadius: 10,
                padding: 14,
                background: "#f9fbfd",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <Typography.Text strong>
                  {t("orderDetail.goodsLine", {
                    number: index + 1,
                    defaultValue: `Goods line ${index + 1}`,
                  })}
                </Typography.Text>
                <Button
                  danger
                  size="small"
                  disabled={goodsLines.length === 1}
                  onClick={() => removeStructuredGood(index)}
                >
                  {t("orderDetail.removeGoodsLine", { defaultValue: "Remove line" })}
                </Button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(180px, 1fr)", gap: 8 }}>
                <Input
                  title={t("orderDetail.goods", { defaultValue: "Goods" })} placeholder={t("orderDetail.goods", { defaultValue: "Goods" })}
                  value={goodsLine.name || ""}
                  onChange={(event) => updateStructuredGoodField(index, "name", event.target.value)}
                />
                <Input
                  title={t("orderDetail.goodsProductId", { defaultValue: "Product ID" })} placeholder={t("orderDetail.goodsProductId", { defaultValue: "Product ID" })}
                  value={goodsLine.productId || ""}
                  onChange={(event) => updateStructuredGoodField(index, "productId", event.target.value)}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  title={t("orderDetail.goodsPackages", { defaultValue: "Number of packages" })} placeholder={t("orderDetail.goodsPackages", { defaultValue: "Number of packages" })}
                  value={goodsLine.numberOfPackages}
                  onChange={(value) => updateStructuredGoodField(index, "numberOfPackages", value)}
                />
                <Input
                  title={t("orderDetail.goodsPackaging", { defaultValue: "Packaging method" })} placeholder={t("orderDetail.goodsPackaging", { defaultValue: "Packaging method" })}
                  value={goodsLine.methodOfPackaging || ""}
                  onChange={(event) => updateStructuredGoodField(index, "methodOfPackaging", event.target.value)}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 1fr 140px", gap: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  title={t("orderDetail.goodsNetWeight", { defaultValue: "Net weight" })} placeholder={t("orderDetail.goodsNetWeight", { defaultValue: "Net weight" })}
                  value={goodsLine.netWeight}
                  onChange={(value) => updateStructuredGoodField(index, "netWeight", value)}
                />
                <Select
                  allowClear
                  options={WEIGHT_UNITS}
                  value={goodsLine.netWeightUnit || undefined}
                  title={t("orderDetail.goodsWeightUnit", { defaultValue: "Weight unit" })} placeholder={t("orderDetail.goodsWeightUnit", { defaultValue: "Weight unit" })}
                  onChange={(value) => updateStructuredGoodField(index, "netWeightUnit", value)}
                />
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  title={t("orderDetail.goodsGrossWeight", { defaultValue: "Gross weight" })} placeholder={t("orderDetail.goodsGrossWeight", { defaultValue: "Gross weight" })}
                  value={goodsLine.weight}
                  onChange={(value) => updateStructuredGoodField(index, "weight", value)}
                />
                <Select
                  allowClear
                  options={WEIGHT_UNITS}
                  value={goodsLine.weightUnit || undefined}
                  title={t("orderDetail.goodsWeightUnit", { defaultValue: "Weight unit" })} placeholder={t("orderDetail.goodsWeightUnit", { defaultValue: "Weight unit" })}
                  onChange={(value) => updateStructuredGoodField(index, "weightUnit", value)}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 1fr 140px", gap: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  title={t("orderDetail.goodsTareWeight", { defaultValue: "Tare weight" })} placeholder={t("orderDetail.goodsTareWeight", { defaultValue: "Tare weight" })}
                  value={goodsLine.tareWeight}
                  onChange={(value) => updateStructuredGoodField(index, "tareWeight", value)}
                />
                <Select
                  allowClear
                  options={WEIGHT_UNITS}
                  value={goodsLine.tareWeightUnit || undefined}
                  title={t("orderDetail.goodsWeightUnit", { defaultValue: "Weight unit" })} placeholder={t("orderDetail.goodsWeightUnit", { defaultValue: "Weight unit" })}
                  onChange={(value) => updateStructuredGoodField(index, "tareWeightUnit", value)}
                />
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  title={t("orderDetail.goodsVolume", { defaultValue: "Volume" })} placeholder={t("orderDetail.goodsVolume", { defaultValue: "Volume" })}
                  value={goodsLine.volume}
                  onChange={(value) => updateStructuredGoodField(index, "volume", value)}
                />
                <Select
                  allowClear
                  options={VOLUME_UNITS}
                  value={goodsLine.volumeUnit || undefined}
                  title={t("orderDetail.goodsVolumeUnit", { defaultValue: "Volume unit" })} placeholder={t("orderDetail.goodsVolumeUnit", { defaultValue: "Volume unit" })}
                  onChange={(value) => updateStructuredGoodField(index, "volumeUnit", value)}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  title={t("orderDetail.goodsCommercialValue", { defaultValue: "Commercial value" })} placeholder={t("orderDetail.goodsCommercialValue", { defaultValue: "Commercial value" })}
                  value={goodsLine.commercialValue}
                  onChange={(value) => updateStructuredGoodField(index, "commercialValue", value)}
                />
                <Select
                  allowClear
                  options={CURRENCY_OPTIONS}
                  value={goodsLine.currency || undefined}
                  title={t("orderDetail.goodsCurrency", { defaultValue: "Currency" })} placeholder={t("orderDetail.goodsCurrency", { defaultValue: "Currency" })}
                  onChange={(value) => updateStructuredGoodField(index, "currency", value)}
                />
              </div>
            </div>
          ))}
          <Button type="dashed" block onClick={addStructuredGood}>
            {t("orderDetail.addGoodsLine", { defaultValue: "Add goods line" })}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default OrderForm;
